import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import type { Card, CardPendingOperation } from "../../../../../../shared/domains/cards/card";
import { CardPendingOperationType, CardStatus } from "../../../../../../shared/domains/cards/card";
import { cardFeatures } from "../../../../../../shared/domains/cards/card-feature-labels";
import { isDefined } from "../../../../../../shared/utils/assert";
import { useRTL } from "../../../../../domain/language/use-rtl";
import { DefaultButton } from "../../../../common/buttons/default-button";
import { ResetIcon } from "../../../../common/svg/reset-icon";
import { shadows, theme } from "../../../../styles/theme";
import { getCardConfigurations } from "../card-configurations";

interface AdditionalInfosSectionProps extends HTMLAttributes<HTMLBaseElement> {
  card: Card;
  onClick: () => void;
}

export function AdditionalInfosSection(props: AdditionalInfosSectionProps) {
  const { card, onClick, className } = props;
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();

  if (!isDefined(card.pendingManagementOperation) && card.status != CardStatus.Opposed) {
    return null;
  }

  const pendingOperationMessage = (infos: CardPendingOperation | undefined) => {
    switch (infos?.operationType) {
      case CardPendingOperationType.Refabrication:
        return formatMessage("card.pendingOperation.refabrication");
      case CardPendingOperationType.CodeReissuing:
        return formatMessage("card.pendingOperation.codeReissuing");
      case CardPendingOperationType.Opposition:
        return formatMessage("card.pendingOperation.opposition");
      case CardPendingOperationType.Blocking:
        return formatMessage("card.pendingOperation.blocking");
      case CardPendingOperationType.Unblocking:
        return formatMessage("card.pendingOperation.unblocking");
      case CardPendingOperationType.Activation:
        return formatMessage("card.pendingOperation.activation");
      case CardPendingOperationType.Creation:
        return formatMessage("card.pendingOperation.creation");
      case CardPendingOperationType.LimitsUpdate:
        return formatMessage("card.pendingOperation.limitsUpdate");
      case CardPendingOperationType.FeaturesUpdate:
        const feature = infos.featureId ? formatMessage(cardFeatures[infos.featureId]) : undefined;
        return feature
          ? formatMessage("card.pendingOperation.featuresUpdate", { feature })
          : formatMessage("card.pendingOperation.unknownFeatureUpdate");
    }
  };

  const { statusColor } = getCardConfigurations(card.status);

  return (
    <GlobalContainer className={className}>
      <LabelContainer $isRTL={isRTL}>
        <Title>{formatMessage("cardsList.infos.title")}</Title>
        <InfosContainer>
          <InfosLabel>
            {card.status === CardStatus.Opposed
              ? formatMessage("cardOptions.cardOpposedMessage")
              : pendingOperationMessage(card.pendingManagementOperation)}
          </InfosLabel>
          {isDefined(card.pendingManagementOperation) ? (
            <RefreshButton onClick={onClick} $isRTL={isRTL}>
              <ResetIcon />
            </RefreshButton>
          ) : null}
        </InfosContainer>
        <StatusDot color={statusColor} $isRTL={isRTL} />
      </LabelContainer>
    </GlobalContainer>
  );
}

const GlobalContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const LabelContainer = styled.div<{ $isRTL: boolean }>`
  flex: 1 0 auto;
  position: relative;
  flex-direction: column;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  padding: ${(props) => (props.$isRTL ? "4px 20px 16px 48px" : "4px 48px 16px 20px")};
  ${shadows.sm};
`;

const Title = styled.h2`
  ${theme.mediumText};
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const InfosContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const InfosLabel = styled.span`
  ${theme.text}
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const StatusDot = styled.div<{ color?: string; $isRTL: boolean }>`
  position: absolute;
  display: flex;
  top: 18px;
  right: ${(props) => (props.$isRTL ? "unset" : "16px")};
  left: ${(props) => (props.$isRTL ? "16px" : "unset")};
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: ${(props) => props.color};
  opacity: 1;
`;

const RefreshButton = styled(DefaultButton)<{ $isRTL: boolean }>`
  left: ${(props) => (props.$isRTL ? 0 : 32)}px;
  right: ${(props) => (props.$isRTL ? 32 : 0)}px;
  top: 2px;
  height: 19px;
  width: 24px;
  svg {
    width: 19px;
    height: 24px;
    object-fit: contain;
    fill: #aaaaaa;
  }
`;
